import React, { useState, useCallback } from 'react'
import api from '../../config/api'
import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  FormControl,
  OutlinedInput,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { PROJECT_NAME } from '../../config/config'

const LoginForm = () => {
  const [phone, setPhone] = useState('+7 ')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  const formatPhone = useCallback((input) => {
    const cleaned = input.replace(/\D/g, '')

    if (!cleaned.startsWith('7') && cleaned.length > 0) {
      return `+7 (${cleaned.substring(0, 3)}`
    }

    const match = cleaned.match(/^7(\d{0,3})(\d{0,3})(\d{0,2})(\d{0,2})/)
    if (!match) return '+7 '

    let formatted = '+7 '
    if (match[1]) formatted += `(${match[1]}`
    if (match[2]) formatted += `) ${match[2]}`
    if (match[3]) formatted += ` ${match[3]}`
    if (match[4]) formatted += `-${match[4]}`

    return formatted
  }, [])

  const handlePhoneChange = useCallback(
    (e) => {
      const input = e.target.value

      if (input.length < 3) {
        setPhone('+7 ')
        return
      }

      const formatted = formatPhone(input)
      setPhone(formatted)
    },
    [formatPhone],
  )

  const normalizePhone = useCallback((formattedPhone) => {
    return formattedPhone.replace(/\D/g, '').replace(/^\+?7/, '7')
  }, [])

  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return

    setError('')
    setIsSubmitting(true)

    try {
      const normalizedPhone = normalizePhone(phone)
      if (normalizedPhone.length !== 11) {
        setError('Номер должен содержать 11 цифр')
        return
      }

      const response = await api.post('/API_FRONT/token/', {
        phone: normalizedPhone,
        password,
      })

      const { access, refresh } = response.data

      localStorage.setItem('access_token', access)
      localStorage.setItem('refresh_token', refresh)

      setSnackbar({
        open: true,
        message: 'Успешный вход!',
        severity: 'success',
      })

      setTimeout(() => {
        window.location.href = '/'
      }, 1000)
    } catch (err) {
      console.error('Ошибка входа:', err)

      let errorMessage = 'Неверный номер телефона или пароль'
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail
      }

      setError(errorMessage)
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [phone, password, isSubmitting, normalizePhone])

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit],
  )

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        p: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url(/back_image.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(8px) brightness(0.7)',
          zIndex: -1,
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          p: 3,
          borderRadius: 2,
          boxShadow: 3,
          backgroundColor: 'background.paper',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Кликабельный логотип FLOWCULT */}
        <Box
          component="a"
          href="/"
          sx={{ textDecoration: 'none', color: 'black' }}
        >
          <Typography variant="h2">{PROJECT_NAME}</Typography>
        </Box>

        <Typography variant="h5" align="center" sx={{ mb: 3 }}>
          Вход в систему
        </Typography>

        {error && (
          <Typography color="error" align="center" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Box component="div">
          <TextField
            fullWidth
            label="Номер телефона"
            value={phone}
            onChange={handlePhoneChange}
            placeholder="+7 (XXX) XXX XX-XX"
            required
            sx={{ mb: 2 }}
            inputProps={{
              maxLength: 18,
            }}
            disabled={isSubmitting}
          />

          <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
            <InputLabel htmlFor="password">Пароль</InputLabel>
            <OutlinedInput
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              inputProps={{
                autoComplete: 'current-password', // Важно для мобильных устройств
                style: {
                  userSelect: 'text', // Разрешаем выделение/вставку
                  WebkitUserSelect: 'text', // Для Safari/iOS
                },
              }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={isSubmitting}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              }
              label="Пароль"
              required
              disabled={isSubmitting}
            />
          </FormControl>

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Войти'
            )}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default LoginForm
